import React, { useEffect, useMemo, useState } from 'react'
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Dropdown,
  DropdownButton,
  Spinner,
} from 'react-bootstrap'
import { Footer, KonektoNavbar } from '../../components'
import './MyCompanyProfile.scss'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { companyService } from '../../services/companyService'
import { getMycompanySuccess } from '../../store/Actions/Company'
import { getMyCompany } from '../../store/Actions'
import { imageService } from '../../services/imageService'
import { COMPANY_TYPES, NUM_OF_EMPLOYEES_OPTIONS } from '../../constants/config'
import { toast } from 'react-toastify'
import { FaClock, FaCheckCircle } from 'react-icons/fa'

export default function MyCompanyProfile() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { token } = useSelector(state => state.authentication)
  const { myCompany } = useSelector(state => state.company)

  const [formData, setFormData] = useState({
    jib: myCompany?.jib ?? '',
    name: myCompany?.name ?? '',
    description: myCompany?.description ?? '',
    phone: myCompany?.phone ?? '',
    address: myCompany?.address ?? '',
    zipCode: myCompany?.zip_code ?? '',
    city: myCompany?.city ?? '',
    email: myCompany?.email ?? '',
    website: myCompany?.website ?? '',
    numOfEmployees: myCompany?.num_of_employees ?? '',
    logoPath: myCompany?.cover_image ?? '',
  })
  const [type, setType] = useState(myCompany?.type?.split(',') ?? [])
  const [previewImage, setPreviewImage] = useState(formData.logoPath)

  const [updateFormErrors, setUpdateFormErrors] = useState(null)
  const [updateResponseErrors, setUpdateResponseErrors] = useState(null)
  const [updateLoader, setUpdateLoader] = useState(false)

  const cities = useSelector(state => state.utility?.cities ?? [])

  const toggleType = option => {
    setType(prevType =>
      prevType.includes(option)
        ? prevType.filter(item => item !== option)
        : [...prevType, option],
    )
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleFileChange = async e => {
    if (e.target.files && e.target.files.length > 0) {
      const formData = new FormData()

      // Loop through all the selected files
      Array.from(e.target.files).forEach(file => {
        formData.append('images[]', file) // Append each file with the correct name ('images[]')
      })

      const response = await imageService.uploadImage(formData)
      if (response.isOk && response.data) {
        // take first path because only one image can be uploaded
        const imagePath = response.data.paths[0]

        setPreviewImage(imagePath)
        setFormData(prevState => ({
          ...prevState,
          logoPath: imagePath,
        }))
      }
    }
  }

  const handleSubmit = async e => {
    setUpdateFormErrors(null)
    e.preventDefault()

    let newErrors = {}

    if (formData?.jib?.trim() === '') {
      newErrors = { ...newErrors, jib: 'JIB ne može biti prazan' }
    }

    if (formData?.name?.trim() === '') {
      newErrors = { ...newErrors, name: 'Ime firme ne može biti prazno' }
    }

    if (formData?.phone?.trim() === '') {
      newErrors = { ...newErrors, phone: 'Telefon ne može biti prazan' }
    }

    if (Object.keys(newErrors).length > 0) {
      setUpdateFormErrors(newErrors)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setUpdateLoader(true)

    const data = {
      name: formData.name,
      description: formData.description,
      phone: formData.phone,
      address: formData.address,
      zip_code: formData.zipCode,
      city: formData.city,
      email: formData.email,
      website: formData.website,
      num_of_employees: formData.numOfEmployees,
      type: type?.join(','),
      cover_image: formData.logoPath,
    }

    const response = await companyService.updateCompany({
      id: myCompany?.id,
      data,
    })
    if (response.isOk && response.data) {
      dispatch(getMycompanySuccess(response.data))
      toast.success('Podaci uspješno sačuvani')
    } else {
      setUpdateResponseErrors(response.message)
    }

    setUpdateLoader(false)
  }

  const companyStatus = useMemo(() => {
    const isVerified = myCompany?.is_verified

    if (isVerified === null) {
      return (
        <span>
          <b>Status:</b>
          {` Verifikacija firme je u toku `}
          <FaClock color="orange" />
        </span>
      )
    } else if (isVerified === 0) {
      return (
        <span style={{ color: '#ff0033' }}>
          <b>Status:</b>
          {` Verifikacija firme nije uspjela. Ažurirajte podatke kako biste ponovo pokrenuli proces verifikacije.`}
        </span>
      )
    } else if (isVerified === 1) {
      return (
        <span>
          <b>Status:</b>
          {` Firma je verifikovana `}
          <FaCheckCircle color="green" />
        </span>
      )
    } else {
      return null
    }
  }, [myCompany])

  useEffect(() => {
    if (!token) {
      navigate('/prijava')
      return
    }

    if (myCompany === null) {
      navigate('/registracija-firme')
      return
    }
  }, [token, myCompany, navigate])

  useEffect(() => {
    if (token) {
      dispatch(getMyCompany())
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }, 200)
  }, [token])

  const typeTitle = type.length > 0 ? type.toString() : 'Vrsta firme'

  return (
    <div id="my-company-profile">
      <KonektoNavbar />
      <Container className="my-5">
        <div className="form-group-wrapper shadow">
          <h3>{formData.name}</h3>
          {companyStatus}
        </div>

        <Form onSubmit={handleSubmit}>
          <div className="form-group-wrapper shadow">
            <h5>Osnovne informacije</h5>

            <hr className="hr divider" />

            <Form.Group controlId="jib" className="mb-3">
              <Form.Label>JIB *</Form.Label>
              <Form.Control
                readOnly
                type="text"
                name="jib"
                placeholder="Jedinstveni identifikacioni broj *"
                value={formData.jib}
                className="opacity-75"
              />
              {updateFormErrors?.jib ? (
                <span className="field-error">{updateFormErrors?.jib}</span>
              ) : null}
            </Form.Group>

            <Form.Group controlId="name" className="mb-3">
              <Form.Label>Ime firme *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Ime firme *"
                value={formData.name}
                onChange={handleChange}
              />
              {updateFormErrors?.name ? (
                <span className="field-error">{updateFormErrors?.name}</span>
              ) : null}
            </Form.Group>

            <Form.Group controlId="description" className="mb-3">
              <Form.Label className="w-50">O firmi</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                placeholder="O firmi"
                value={formData.description}
                onChange={handleChange}
                style={{ height: '150px' }} // Set the specific height for the textarea
              />
            </Form.Group>

            <Form.Group controlId="phone" className="mb-3">
              <Form.Label>Telefon *</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                placeholder="Telefon *"
                value={formData.phone}
                onChange={handleChange}
              />
              {updateFormErrors?.phone ? (
                <span className="field-error">{updateFormErrors?.phone}</span>
              ) : null}
            </Form.Group>
          </div>

          <div className="form-group-wrapper shadow">
            <h5>Lokacija</h5>
            <hr className="hr divider" />
            <Form.Group controlId="address" className="mb-3">
              <Form.Label>Adresa</Form.Label>
              <Form.Control
                type="text"
                name="address"
                placeholder="Adresa"
                value={formData.address}
                onChange={handleChange}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group controlId="zip_code" className="mb-3">
                  <Form.Label>Poštanski broj</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipCode"
                    placeholder="Poštanski broj"
                    value={formData.zipCode}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="type" className="mb-3">
                  <Form.Label>Grad</Form.Label>
                  <Form.Select
                    title="Grad"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}>
                    <option key="default" value="" disabled>
                      Izaberite grad
                    </option>

                    {cities.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="form-group-wrapper shadow">
            <h5>Dodatne informacije</h5>
            <hr className="hr divider" />
            <Form.Group controlId="image" className="mb-3">
              <Form.Label>Logo firme</Form.Label>
              <div className="custom-file-upload">
                <input
                  type="file"
                  name="images"
                  id="image"
                  accept="image/*"
                  className="d-none"
                  onChange={handleFileChange} // Create this function to handle file selection
                />
                <label htmlFor="image" className="btn btn-outline-secondary">
                  Izaberite sliku
                </label>
              </div>
              {previewImage && (
                <div className="mt-3">
                  <img src={previewImage} alt="Odabrana slika" />
                </div>
              )}
            </Form.Group>

            <Form.Group controlId="email" className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="korisnicko_ime@mail.com"
                value={formData.email}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group controlId="website" className="mb-3">
              <Form.Label>Web stranica</Form.Label>
              <Form.Control
                type="text"
                name="website"
                placeholder="https://moja-stranica.ba"
                value={formData.website}
                onChange={handleChange}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group controlId="type" className="mb-3">
                  <Form.Label>Broj uposlenika</Form.Label>
                  <Form.Select
                    title="Broj uposlenika"
                    name="numOfEmployees"
                    value={formData.numOfEmployees}
                    onChange={handleChange}>
                    {NUM_OF_EMPLOYEES_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="type" className="mb-3">
                  <Form.Label>Vrsta firme</Form.Label>
                  <DropdownButton
                    title={<span className="dropdown-title">{typeTitle}</span>}
                    className="custom-dropdown-button"
                    variant="outline-secondary"
                    autoClose="outside">
                    {COMPANY_TYPES.map((option, index) => (
                      <Dropdown.Item
                        as="button"
                        key={index}
                        onClick={e => {
                          e.preventDefault()
                          toggleType(option)
                        }}
                        className="d-flex justify-content-between align-items-center custom-dropdown-item">
                        {option}
                        <input
                          type="checkbox"
                          checked={type.includes(option)}
                          readOnly
                          className="ms-2"
                        />
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {updateResponseErrors ? (
            <p className="field-error text-center mb-0 mt-2">
              {updateResponseErrors}
            </p>
          ) : null}

          <div className="text-end">
            <Button
              disabled={updateLoader}
              variant="primary"
              type="submit"
              className="mt-3 rounded-pill primary-button">
              {updateLoader ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                'Sačuvaj'
              )}
            </Button>
          </div>
        </Form>
      </Container>
      <Footer />
    </div>
  )
}
